import { computed, inject, Injectable } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { ReportsService } from '../../Services/reports.service';
import { GetEntraAdminRolesReport } from '../../Interfaces/api/reports.interface';
import { RoleAssignment } from '../../Models/avyna/entra-admin-roles-report.model';
import { ChartDatum } from '../../Modules/Reports/models/report-dashboard.types';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';

const GLOBAL_ADMINISTRATOR_ROLE = 'Global Administrator';
const DISABLED_ACCOUNT_STATUS = 'Disabled';
const GLOBAL_ADMIN_WARNING_THRESHOLD = 5;
const TOP_ROLES_LIMIT = 10;

@Injectable()
export class EntraAdminRolesReportFacade {
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _reportsService = inject(ReportsService);

  private readonly _resource = httpResource<GetEntraAdminRolesReport>(() =>
    this._reportsService.entraAdminRoles()
  );

  readonly isLoading = computed(() => this._resource.isLoading());
  readonly error = computed(() => this._resource.error());
  readonly data = computed(() => this._resourceState.valueOf(this._resource)?.data ?? null);

  readonly permanentUserAssignments = computed(() => this.data()?.permanentUserAssignments ?? []);
  readonly eligibleUserAssignments = computed(() => this.data()?.eligibleUserAssignments ?? []);
  readonly groupAssignments = computed(() => this.data()?.groupAssignments ?? []);
  readonly servicePrincipalAssignments = computed(() => this.data()?.servicePrincipalAssignments ?? []);
  readonly pimAuditLogs = computed(() => this.data()?.pimAuditLogs ?? []);

  readonly permanentCount = computed(() => this.permanentUserAssignments().length);
  readonly eligibleCount = computed(() => this.eligibleUserAssignments().length);

  readonly privilegedIdentityCount = computed(() =>
    this.countDistinctPrincipals(this.allUserAssignments())
  );

  readonly globalAdminCount = computed(() =>
    this.countDistinctPrincipals(this.globalAdminAssignments())
  );

  readonly globalAdminWarning = computed(() => this.globalAdminCount() > GLOBAL_ADMIN_WARNING_THRESHOLD);

  readonly allUserAssignmentRows = computed<RoleAssignment[]>(() => this.allUserAssignments());

  readonly globalAdminAssignmentRows = computed<RoleAssignment[]>(() => this.globalAdminAssignments());

  readonly disabledWithRoles = computed<RoleAssignment[]>(() =>
    this.permanentUserAssignments().filter(row => row.accountStatus === DISABLED_ACCOUNT_STATUS)
  );

  readonly assignmentTypeDonutData = computed<ChartDatum[]>(() => [
    { name: 'app.reports.entra_admin_roles.permanent_assignments', value: this.permanentCount(), key: 'permanentUserAssignments' },
    { name: 'app.reports.entra_admin_roles.eligible_assignments', value: this.eligibleCount(), key: 'eligibleUserAssignments' },
  ]);

  readonly roleBarData = computed<ChartDatum[]>(() => this.buildRoleBarData());

  readonly activityFeed = computed(() => this.pimAuditLogs());

  reload(): void {
    this._resource.reload();
  }

  private allUserAssignments(): RoleAssignment[] {
    return [...this.permanentUserAssignments(), ...this.eligibleUserAssignments()];
  }

  private globalAdminAssignments(): RoleAssignment[] {
    return this.allUserAssignments().filter(row => row.roleName === GLOBAL_ADMINISTRATOR_ROLE);
  }

  private countDistinctPrincipals(rows: RoleAssignment[]): number {
    return new Set(rows.map(row => row.principalId)).size;
  }

  private buildRoleBarData(): ChartDatum[] {
    const countByRole = new Map<string, number>();
    for (const row of this.allUserAssignments()) {
      countByRole.set(row.roleName, (countByRole.get(row.roleName) ?? 0) + 1);
    }
    return [...countByRole.entries()]
      .sort((left, right) => right[1] - left[1])
      .slice(0, TOP_ROLES_LIMIT)
      .map(([name, value]) => ({ name, value }));
  }
}
